import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
    ChevronLeft, 
    Flame,
    Leaf,
    AlertCircle,
    Coffee,
    Wine,
    Cake,
    Soup,
    UtensilsCrossed,
    Sun,
    Grid3X3,
    Salad,
    GlassWater,
    Sparkles
} from 'lucide-react';
import { useSettings } from '../components/ClientLayout';

// ============================================================
// ALDRIN RESTAURANT - PAGE MENU ÉLÉGANTE
// Design: Carte de restaurant haut de gamme
// ============================================================

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Logo officiel
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';

// Couleurs
const BRAND_GREEN = '#1a3d2c';

// Catégories avec icônes - ORDRE EXACT SOUHAITÉ
const MENU_CATEGORIES = [
    { id: 'petit_dejeuner', name: 'Petit-déjeuner', icon: Sun },
    { id: 'entrees', name: 'Entrées', icon: Salad },
    { id: 'plats', name: 'Plats', icon: Soup },
    { id: 'desserts', name: 'Desserts', icon: Cake },
    { id: 'boissons_fraiches', name: 'Boissons fraîches', icon: GlassWater },
    { id: 'boissons_chaudes', name: 'Boissons chaudes', icon: Coffee },
    { id: 'cocktails', name: 'Cocktails', icon: Wine },
    { id: 'formules', name: 'Formules', icon: UtensilsCrossed },
];

// ============================================================
// CARTE DE PLAT - Design Premium Restaurant
// Structure: Photo plein cadre → Nom → Description → Prix
// ============================================================

const ProductCard = ({ product, showPrices }) => {
    const [imageError, setImageError] = useState(false);
    const hasImage = product.image_url && !imageError;
    const isUnavailable = !product.is_available;
    
    return (
        <div 
            className={`group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 ${isUnavailable ? 'opacity-60' : ''}`}
            style={{ 
                boxShadow: '0 4px 20px rgba(26, 61, 44, 0.08)',
            }}
        >
            {/* Photo plein cadre */}
            <div className="aspect-[4/3] relative overflow-hidden">
                {hasImage ? (
                    <img
                        src={product.image_url}
                        alt={product.name}
                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${isUnavailable ? 'grayscale' : ''}`}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                            background: `linear-gradient(135deg, ${BRAND_GREEN}08 0%, ${BRAND_GREEN}15 100%)` 
                        }}
                    >
                        <span 
                            className="text-5xl font-light tracking-wider"
                            style={{ color: `${BRAND_GREEN}30` }}
                        >
                            {product.name?.charAt(0)?.toUpperCase()}
                        </span>
                    </div>
                )}
                
                {/* Badges discrets en haut à gauche */}
                {(product.is_popular || product.is_spicy || product.is_vegetarian) && (
                    <div className="absolute top-3 left-3 flex gap-1.5">
                        {product.is_popular && (
                            <span 
                                className="px-2.5 py-1 text-white text-xs font-medium rounded-full backdrop-blur-sm"
                                style={{ backgroundColor: `${BRAND_GREEN}cc` }}
                            >
                                Populaire
                            </span>
                        )}
                        {product.is_spicy && (
                            <span className="p-1.5 bg-red-500/90 text-white rounded-full backdrop-blur-sm">
                                <Flame className="w-3.5 h-3.5" />
                            </span>
                        )}
                        {product.is_vegetarian && (
                            <span className="p-1.5 bg-emerald-500/90 text-white rounded-full backdrop-blur-sm">
                                <Leaf className="w-3.5 h-3.5" />
                            </span>
                        )}
                    </div>
                )}
                
                {/* Badge indisponible */}
                {isUnavailable && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] flex items-center justify-center">
                        <span 
                            className="px-4 py-2 rounded-full text-white text-sm font-medium flex items-center gap-2"
                            style={{ backgroundColor: BRAND_GREEN }}
                        >
                            <AlertCircle className="w-4 h-4" />
                            Indisponible
                        </span>
                    </div>
                )}
            </div>
            
            {/* Contenu : Nom, Description, Prix */}
            <div className="p-4">
                {/* Nom du plat */}
                <h3 
                    className="font-semibold text-base mb-2 line-clamp-1 tracking-wide"
                    style={{ color: BRAND_GREEN }}
                >
                    {product.name}
                </h3>
                
                {/* Description */}
                <p className="text-gray-500 text-sm line-clamp-2 mb-3 min-h-[2.5rem] leading-relaxed">
                    {product.client_description || product.description || 'Délicieux plat préparé avec soin'}
                </p>
                
                {/* Prix */}
                {showPrices && (
                    <div className="pt-2 border-t border-gray-100">
                        <span 
                            className={`font-bold text-lg ${isUnavailable ? 'text-gray-400' : ''}`}
                            style={{ color: isUnavailable ? undefined : BRAND_GREEN }}
                        >
                            {product.price?.toLocaleString('fr-FR')} F
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Boutons de catégories - Design Premium
const CategoryButtons = ({ categories, activeCategory, onSelect }) => {
    return (
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = activeCategory === cat.id;
                
                return (
                    <button
                        key={cat.id}
                        onClick={() => onSelect(cat.id)}
                        className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                            isActive 
                                ? 'text-white shadow-lg' 
                                : 'text-gray-500 hover:bg-gray-50'
                        }`}
                        style={isActive 
                            ? { 
                                backgroundColor: BRAND_GREEN,
                                boxShadow: `0 4px 14px ${BRAND_GREEN}40`
                            } 
                            : { 
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb'
                            }
                        }
                    >
                        <Icon className="w-4 h-4" />
                        <span className="tracking-wide">{cat.name}</span>
                    </button>
                );
            })}
        </div>
    );
};

// Page Menu principale
const ClientMenuPage = () => {
    const { settings } = useSettings();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('plats'); // Par défaut "Plats"
    
    const showPrices = settings?.show_prices !== false;
    
    useEffect(() => {
        const fetchAllProducts = async () => {
            try {
                setLoading(true);
                
                // Charger le menu principal et les cocktails en parallèle
                const [menuResponse, cocktailsResponse] = await Promise.all([
                    axios.get(`${API_URL}/api/client/menu`).catch(() => ({ data: [] })),
                    axios.get(`${API_URL}/api/cocktails/client/cocktails`).catch(() => ({ data: [] }))
                ]);
                
                // Produits du menu principal
                const menuProducts = (menuResponse.data || []).filter(p => 
                    p.is_available || p.show_when_unavailable !== false
                );
                
                // Cocktails - convertir au même format que les produits
                const cocktailProducts = (cocktailsResponse.data || []).map(c => ({
                    ...c,
                    category: 'cocktails',
                    category_name: 'Cocktails',
                    client_description: c.description
                }));
                
                // Combiner les deux
                setProducts([...menuProducts, ...cocktailProducts]);
            } catch (error) {
                console.error('Error fetching menu:', error);
                setProducts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAllProducts();
    }, []);
    
    const filteredProducts = useMemo(() => {
        let result = products;
        
        // Filtrer par catégorie - vérifie category ET category_name
        result = result.filter(p => {
            const catKey = (p.category_key || p.category || '').toLowerCase().replace(/[éèê]/g, 'e');
            const catName = (p.category_name || '').toLowerCase().replace(/[éèê]/g, 'e');
            const activeCat = activeCategory.toLowerCase().replace(/[éèê]/g, 'e');
            
            // Match direct ou partiel (normalisé sans accents)
            if (catKey === activeCat || catKey.includes(activeCat)) return true;
            if (catName.includes(activeCat)) return true;
            if (activeCat.includes(catKey) && catKey.length > 3) return true;
            
            // Aliases spécifiques pour chaque catégorie
            if (activeCat === 'petit_dejeuner') {
                if (catKey.includes('petit') || catName.includes('petit')) return true;
                if (catKey.includes('dejeuner') || catName.includes('dejeuner')) return true;
                if (catKey.includes('breakfast') || catName.includes('breakfast')) return true;
            }
            if (activeCat === 'boissons_chaudes') {
                if ((catKey.includes('chaud') || catName.includes('chaud')) && (catKey.includes('boisson') || catName.includes('boisson') || catKey.includes('cafe') || catKey.includes('the'))) return true;
            }
            if (activeCat === 'boissons_fraiches') {
                const isBeverage = catKey.includes('boisson') || catKey.includes('jus') || catKey.includes('eau') || catName.includes('fraiche') || catKey.includes('gazeuse') || catKey.includes('sucree');
                const isNotHot = !catKey.includes('chaud') && !catName.includes('chaud');
                if (isBeverage && isNotHot) return true;
            }
            if (activeCat === 'entrees') {
                if (catKey.includes('entree') || catName.includes('entree')) return true;
            }
            if (activeCat === 'formules') {
                if (catKey.includes('menu') || catKey.includes('formule') || catName.includes('formule')) return true;
            }
            if (activeCat === 'cocktails') {
                if (catKey.includes('cocktail') || catName.includes('cocktail')) return true;
            }
            if (activeCat === 'plats') {
                if (catKey.includes('plat') || catName.includes('plat')) return true;
                if (catKey.includes('grill') || catName.includes('grill')) return true;
                if (catKey.includes('viande') || catKey.includes('poisson')) return true;
            }
            if (activeCat === 'desserts') {
                if (catKey.includes('dessert') || catName.includes('dessert')) return true;
            }
            
            return false;
        });
        
        return result.sort((a, b) => {
            if (a.is_available && !b.is_available) return -1;
            if (!a.is_available && b.is_available) return 1;
            if (a.is_popular && !b.is_popular) return -1;
            if (!a.is_popular && b.is_popular) return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [products, activeCategory]);
    
    return (
        <div className="min-h-screen bg-white">
            
            {/* Header Premium */}
            <header className="bg-white border-b border-gray-100">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                    
                    {/* Bouton retour discret */}
                    <div className="flex justify-start mb-4">
                        <Link 
                            to="/client" 
                            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                            style={{ color: `${BRAND_GREEN}80` }}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Retour
                        </Link>
                    </div>
                    
                    {/* Logo centré - GRAND */}
                    <div className="flex justify-center mb-6">
                        <Link to="/client">
                            <img 
                                src={LOGO_URL} 
                                alt="Aldrin Restaurant" 
                                className="w-[220px] sm:w-[280px] lg:w-[340px] object-contain"
                                data-testid="menu-logo"
                            />
                        </Link>
                    </div>
                    
                    {/* Ligne décorative élégante */}
                    <div 
                        className="w-12 h-[2px] mx-auto mb-5"
                        style={{ backgroundColor: BRAND_GREEN }}
                    />
                    
                    {/* Titre */}
                    <h1 
                        className="text-2xl sm:text-3xl font-light text-center tracking-[0.15em] uppercase mb-8"
                        style={{ color: BRAND_GREEN }}
                    >
                        Notre Carte
                    </h1>
                    
                    {/* Catégories */}
                    <CategoryButtons
                        categories={MENU_CATEGORIES}
                        activeCategory={activeCategory}
                        onSelect={setActiveCategory}
                    />
                </div>
            </header>
            
            {/* Grille de plats - Design Premium */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                {loading ? (
                    // Skeleton loader élégant
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div 
                                key={i} 
                                className="bg-white rounded-2xl overflow-hidden animate-pulse"
                                style={{ boxShadow: '0 4px 20px rgba(26, 61, 44, 0.06)' }}
                            >
                                <div className="aspect-[4/3] bg-gray-100" />
                                <div className="p-4 space-y-3">
                                    <div className="h-5 bg-gray-100 rounded-lg w-3/4" />
                                    <div className="space-y-2">
                                        <div className="h-3 bg-gray-100 rounded w-full" />
                                        <div className="h-3 bg-gray-100 rounded w-2/3" />
                                    </div>
                                    <div className="pt-2 border-t border-gray-50">
                                        <div className="h-6 bg-gray-100 rounded w-1/3" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-16">
                        <div 
                            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${BRAND_GREEN}10` }}
                        >
                            <UtensilsCrossed className="w-10 h-10" style={{ color: BRAND_GREEN }} />
                        </div>
                        <h3 
                            className="text-2xl font-light mb-3 tracking-wide"
                            style={{ color: BRAND_GREEN }}
                        >
                            Aucun plat disponible
                        </h3>
                        <p className="text-gray-400 text-base">
                            Sélectionnez une autre catégorie
                        </p>
                    </div>
                ) : (
                    // Grille responsive : 2 colonnes mobile, 3 tablette, 4 desktop
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                        {filteredProducts.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                showPrices={showPrices}
                            />
                        ))}
                    </div>
                )}
            </main>
            
            {/* Footer élégant */}
            <footer className="py-10 text-center border-t border-gray-100 bg-white">
                <p 
                    className="text-sm tracking-wide"
                    style={{ color: `${BRAND_GREEN}60` }}
                >
                    Pour commander, adressez-vous à notre équipe
                </p>
            </footer>
        </div>
    );
};

export default ClientMenuPage;
